<template>
    <div class="submit-project">
        <div class="submit-project__header">
            <h2>Projekt einreichen</h2>
            <p style="color: #004494; text-align: center; font-weight: bold; " v-show="!projectShowErrorDesc && !projectError">
                Wenn nicht anders angegeben, müssen alle Felder ausgefüllt werden.
            </p>
            <p id="error-desc" style="font-weight: bold; color: #d11212; text-align: center;" v-show="projectShowErrorDesc">
                Bitte füllen Sie alle rot markierten Felder aus.
            </p>
            <p id="error-desc-unknown" style="font-weight: bold; color: #d11212; text-align: center;" v-show="projectError">
                Ein unbekannter Fehler ist aufgetreten.
            </p>
        </div>

        <div class="submit-project__step" id="submit-form">
                <div class="submit-project__step--title">
                    <h3>{{formCurrentStep + 1}}. {{formSteps[formCurrentStep].name}}</h3>
                </div>

                <div class="submit-project__step--progress">
                    <div class="submit-project__step--progress__inner" :style="'width: ' + progressBarWidthStep + '%'"></div>
                </div>

                <div v-if="!isProjectLoading && !projectCreated" class="submit-project__form">
                    <div class="submit-project__form-inner">
                        <form ref="form" @submit.prevent.stop style="position: relative;">

                            <transition :name="swipeDirection" mode="out-in">
                                <!-- Step 1 -->
                                <div class="submit-project__step--first" v-if="formCurrentStep === 0" key="firstStep">
                                    <div class="form-block">
                                        <label class="form-block__label" for="title">Titel des Projekts</label>
                                        <input-custom
                                            v-model="$v.form.step_1.titel.$model"
                                            :error="$v.form.step_1.titel.$error || projectNameAlreadyTaken === this.form.step_1.titel"
                                            :label="false"
                                            customInvalid
                                            customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie 2-255 Zeichen ein."
                                            placeholder="z.B.: Treue Vierbeiner brauchen mehr Platz und eine neue Pflegestation"
                                            aria-label="Titel des Projekts"
                                        >
                                            <div class="invalid-feedback" slot="error" v-if="projectNameAlreadyTaken === this.form.step_1.titel">
                                                Dieser Projektname ist bereits vergeben. Bitte wählen Sie einen anderen Namen.
                                            </div>
                                        </input-custom>

                                        <div class="form-block__description">
                                            Sie wollen begeistern! Deshalb muss der Titel Ihres Projektes gleichzeitig informieren und neugierig machen.
                                        </div>
                                    </div>

                                    <div class="form-block">
                                        <label class="form-block__label" for="short-description">Kurzbeschreibung</label>
                                        <div class="form-group">
                                            <textarea-custom
                                                v-model="$v.form.step_1.kurzbeschreibung.$model"
                                                :error="$v.form.step_1.kurzbeschreibung.$error"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie maximal 160 Zeichen ein."
                                                :max-length="160"
                                                :max-length-counter="true"
                                                aria-label="Kurzbeschreibung"
                                            ></textarea-custom>
                                        </div>
                                        <div class="form-block__description">
                                            Beschreiben Sie Ihr Projekt in einem kurzen Fließtext mit max. 160 Zeichen (inkl. Leerzeichen). Dieser Text wird in der Projektübersicht angezeigt.
                                        </div>
                                    </div>

                                    <div class="form-block">
                                        <label class="form-block__label" for="description">Ausführliche Beschreibung</label>
                                        <div class="form-group">
                                            <textarea-custom
                                                v-model="$v.form.step_1.ausfuehrliche_beschreibung.$model"
                                                :error="$v.form.step_1.ausfuehrliche_beschreibung.$error"
                                                aria-label="Ausführliche Beschreibung"
                                            ></textarea-custom>

                                        </div>
                                        <div class="form-block__description">
                                            Erzählen Sie möglichst genau, lebendig und anschaulich von Ihrem Projekt und dem Bestimmungszweck der Spenden. Motivieren Sie zum Spenden, indem Sie deutlich machen, was dadurch möglich gemacht wird.
                                        </div>
                                    </div>
                                </div>
                                <!-- Step 1 -->

                                <!-- Step 2 -->
                                <div class="submit-project__step--second" v-if="formCurrentStep === 1" key="secondStep">
                                    <div class="form-block">
                                        <label class="form-block__label" for="project-image">Projektbild</label>
                                        <div class="form-group">
                                            <input-file accept="jpg,png,gif,jpeg" :error="$v.form.step_2.bild.$error" @input="setFile('bild', $event)" :stored-files="$v.form.step_2.$model.bild ? [$v.form.step_2.$model.bild] : []" :requirements="{maxSize: 10485760, minSize: 0}">
                                                <span><strong>Projektbild hier ablegen oder klicken und auswählen.</strong><br> Folgende Dateiformate sind möglich jpg, png, gif. <br> <strong>Dateigröße: maximal 10 MB</strong></span>
                                            </input-file>
                                            <div class="invalid-feedback">
                                                Dieses Feld ist ein Pflichtfeld.
                                            </div>
                                        </div>
                                        <div class="form-block__error" v-if="$v.form.step_2.bild.$error">
                                            Bitte laden Sie eine Datei in folgendem Format hoch: .jpg, .png, .gif.
                                        </div>

                                        <div class="form-block__description">
                                            Das Bild wird in der Übersicht und auf der Projektseite angezeigt. Es muss eine Mindestbreite von 800 Pixeln haben.
                                            Sie müssen die Urheberrechte an dem Bild haben (siehe
                                            <router-link :to="{name: 'ParticipationTerms'}" target="_blank">
                                                Teilnahmebedingungen
                                            </router-link>
                                            ).
                                        </div>
                                        <div class="form-block__description">
                                            <strong>Bitte beachten Sie:</strong> KI-generierte Bilder müssen gekennzeichnet werden. Bitte geben Sie hier an, wenn Sie ein KI-generiertes Bild hochladen.
                                        </div>
                                        <div class="form-block__description">
                                            <input type="checkbox" id="bild-ki-generiert" v-model="form.step_2.bild_ki_generiert">
                                            <label for="bild-ki-generiert">Mein Bild ist KI-generiert</label>
                                        </div>
                                    </div>

                                    <div class="form-block">
                                        <label class="form-block__label" for="exemption-modest">Freistellungsbescheid</label>
                                        <div class="form-group">
                                            <input-file :error="$v.form.step_2.freistellungsbescheid.$error" @input="setFile('freistellungsbescheid', $event)" :stored-files="$v.form.step_2.$model.freistellungsbescheid ? [$v.form.step_2.$model.freistellungsbescheid] : []" :requirements="{maxSize: 10485760, minSize: 0}">
                                                <span><strong>Freistellungsbescheid hier ablegen oder klicken und auswählen.</strong><br> Folgende Dateiformate sind möglich jpg, png, gif, pdf, doc, docx, txt. <br> <strong>Dateigröße: maximal 10 MB</strong></span>
                                            </input-file>
                                        </div>
                                        <div class="form-block__description">
                                            Der Freistellungsbescheid weist die Gemeinnützigkeit Ihrer Organisation nach.
                                        </div>
                                    </div>

                                    <div class="form-block">
                                        <label class="form-block__label" for="logo">Logo</label>
                                        <div class="form-group">
                                            <input-file accept="jpg,png,gif,jpeg" :error="$v.form.step_2.logo.$error" @input="setFile('logo', $event)" :stored-files="$v.form.step_2.$model.logo ? [$v.form.step_2.$model.logo] : []" :requirements="{maxSize: 10485760, minSize: 0}">
                                                <span><strong>Logo hier ablegen oder klicken und auswählen.</strong><br> Folgende Dateiformate sind möglich jpg, png, gif. <br> <strong>Dateigröße: maximal 10 MB</strong></span>
                                            </input-file>
                                        </div>
                                        <div class="form-block__error" v-if="$v.form.step_2.logo.$error">
                                            Bitte laden Sie eine Datei in folgendem Format hoch: .jpg, .png, .gif.
                                        </div>
                                        <div class="form-block__description">
                                            Das Logo wird auf der Projektseite angezeigt. Das Bild muss eine Mindestbreite von 500 Pixeln haben.
                                        </div>
                                    </div>
                                </div>
                                <!-- Step 2 -->

                                <!-- Step 3 -->
                                <div class="submit-project__step--third" v-if="formCurrentStep === 2" key="thirdStep">
                                    <div class="form-block">
                                        <label class="form-block__label" for="organisation">Ihre Kontaktdaten</label>
                                        <div class="form-row">
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_organisation.$model"
                                                :error="$v.form.step_3.projekttraeger_organisation.$error"
                                                class="col-12 col-lg-12"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie 2-255 Zeichen ein."
                                                aria-label="Organisation"
                                            >
                                                Organisation
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_strasse.$model"
                                                :error="$v.form.step_3.projekttraeger_strasse.$error"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie 2-255 Zeichen ein."
                                                class="col-12 col-lg-8"
                                                aria-label="Straße"
                                            >
                                                Straße
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_nummer.$model"
                                                :error="$v.form.step_3.projekttraeger_nummer.$error"
                                                class="col-12 col-lg-4"
                                                aria-label="Hausnummer"
                                            >
                                                Hausnr.
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_plz.$model"
                                                :error="$v.form.step_3.projekttraeger_plz.$error"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie eine gültige PLZ ein."
                                                class="col-12 col-lg-4"
                                                aria-label="Postleitzahl"
                                            >
                                                PLZ
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_ort.$model"
                                                :error="$v.form.step_3.projekttraeger_ort.$error"
                                                class="col-12 col-lg-8"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie 2-255 Zeichen ein."
                                                aria-label="Ort"
                                            >
                                                Ort
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_vorname.$model"
                                                :error="$v.form.step_3.projekttraeger_vorname.$error"
                                                class="col-12 col-lg-6"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie 2-255 Zeichen ein."
                                                aria-label="Vorname"
                                            >
                                                Vorname
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_nachname.$model"
                                                :error="$v.form.step_3.projekttraeger_nachname.$error"
                                                class="col-12 col-lg-6"
                                                aria-label="Nachname"
                                            >
                                                Nachname
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_telefonnummer.$model"
                                                :error="$v.form.step_3.projekttraeger_telefonnummer.$error"
                                                class="col-12 col-lg-6"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie 2-255 Zeichen ein."
                                                aria-label="Telefonnummer"
                                            >
                                                Telefonnummer
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_email.$model"
                                                :error="$v.form.step_3.projekttraeger_email.$error"
                                                :customInvalid="!$v.form.step_3.projekttraeger_email.email"
                                                customInvalidMessage="Bitte geben Sie eine gültige E-Mail-Adresse ein."
                                                type="email"
                                                class="col-12 col-lg-6"
                                                aria-label="E-Mail Adresse"
                                            >
                                                E-Mail Adresse
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projekttraeger_internetadresse.$model"
                                                :error="$v.form.step_3.projekttraeger_internetadresse.$error"
                                                :customInvalid="!$v.form.step_3.projekttraeger_internetadresse.url || urlInvalid"
                                                customInvalidMessage="Bitte geben Sie eine gültige Internetadresse ein."
                                                type="text"
                                                class="col-12 col-lg-12"
                                                aria-label="Internetadresse"
                                            >
                                                Internetadresse
                                                <small class="form-text text-muted" slot="description">
                                                    Beispiel: https://www.ihre-internetseite.de
                                                </small>
                                            </input-custom>
                                            <div class="form-group col-lg-12">
                                                <div class="form-check">
                                                    <input type="checkbox" id="project-location-visible" class="form-check-input" v-model="projectLocationVisible">
                                                    <label for="project-location-visible" class="form-check-label">Abweichender Projektort</label>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-block__description">
                                            Diese Angaben werden als Kontakt zu Ihrem Projekt genannt. Wenn sich Ihr Projekt auch an der hier genannten Adresse befindet, wird aus diesen Angaben ebenfalls der Eintrag in der Übersichtskarte erstellt.
                                            <br><br>
                                            Falls sich Ihr Projekt nicht am selben Ort wie die Organisation befindet, wählen Sie bitte "abweichender Projektort".
                                        </div>
                                    </div>
                                    <div
                                        v-if="projectLocationVisible"
                                        class="form-block"
                                    >
                                        <label class="form-block__label" for="logo">Projektort</label>
                                        <div class="form-row">
                                            <input-custom
                                                v-model="$v.form.step_3.projektort_strasse.$model"
                                                :error="$v.form.step_3.projektort_strasse.$error"
                                                type="text"
                                                class="col-12 col-lg-8"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie 2-255 Zeichen ein."
                                                aria-label="Straße"
                                            >
                                                Straße
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projektort_nummer.$model"
                                                :error="$v.form.step_3.projektort_nummer.$error"
                                                type="text"
                                                class="col-12 col-lg-4"
                                                aria-label="Hausnummer"
                                            >
                                                Hausnr.
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projektort_plz.$model"
                                                :error="$v.form.step_3.projektort_plz.$error"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie eine gültige PLZ ein."
                                                type="text"
                                                class="col-12 col-lg-4"
                                                aria-label="Postleitzahl"
                                            >
                                                PLZ
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_3.projektort_ort.$model"
                                                :error="$v.form.step_3.projektort_ort.$error"
                                                type="text"
                                                class="col-12 col-lg-8"
                                                customInvalid
                                                customInvalidMessage="Dieses Feld ist ein Pflichtfeld. Geben Sie 2-255 Zeichen ein."
                                                aria-label="Ort"
                                            >
                                                Ort
                                            </input-custom>
                                        </div>
                                        <div class="form-block__description">
                                            Aus diesen Angaben wird der Eintrag in der Übersichtskarte erstellt.
                                        </div>
                                    </div>

                                </div>
                                <!-- Step 3 -->

                                <!-- Step 4 -->
                                <div class="submit-project__step--fourth" v-if="formCurrentStep === 3" key="fourthStep">
                                    <div class="form-block">
                                        <label class="form-block__label" for="donation-target">Spendenziel</label>
                                        <div class="form-group">
                                            <div id="donation-target__wrapper" :class="{'is-invalid': $v.form.step_4.spendenziel.$error}">
                                                <div id="donation-target">
                                                    <range-slider
                                                        :min="0"
                                                        :max="500"
                                                        :step="10"
                                                        v-model="$v.form.step_4.spendenziel.$model"
                                                    >
                                                    </range-slider>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="form-block__description">
                                            Geben Sie hier bitte an, welche Summe Sie benötigen, um Ihr Projekt umsetzen zu können.
                                        </div>
                                    </div>

                                    <div class="form-block">
                                        <label class="form-block__label" for="iban">Bankverbindung</label>
                                        <div class="form-row">
                                            <input-custom
                                                :value="$v.form.step_4.bankverbindung_iban.$model"
                                                @input="$v.form.step_4.bankverbindung_iban.$model = $event.replace(/\s/g, '')"
                                                :error="$v.form.step_4.bankverbindung_iban.$error"
                                                type="text"
                                                class="col-12 col-lg-6"
                                            >
                                                IBAN
                                                <div class="invalid-feedback" slot="error">
                                                    Der IBAN muss zwischen 15 und 34 Zeichen lang sein.
                                                </div>
                                            </input-custom>
                                            <input-custom
                                                v-model="$v.form.step_4.bankverbindung_bic.$model"
                                                :error="$v.form.step_4.bankverbindung_bic.$error"
                                                type="text"
                                                class="col-12 col-lg-6"
                                            >
                                                BIC
                                                <div class="invalid-feedback" slot="error">
                                                    Der BIC muss zwischen 8 und 11 Zeichen lang sein.
                                                </div>
                                            </input-custom>
                                        </div>
                                        <div class="form-block__description">
                                            Die Bankverbindung wird bei der Beschreibung Ihres Projektes angezeigt – für den Fall, dass jemand außer den Herzen noch mehr für Ihr Projekt spenden möchte.
                                        </div>
                                    </div>
                                </div>
                                <!-- Step 4 -->

                                <!-- Step 5 -->
                                <div class="submit-project__step--fifth" v-if="formCurrentStep === 4" key="fifthStep">
                                    <div class="form-block">
                                        <label class="form-block__label" for="exemption-modest">Jetzt mitmachen!</label>
                                        <div class="form-block__description" style="padding-bottom: 5px;">
                                            Ich versichere, dass ich berechtigt bin, das Projekt einzureichen. Ich stimme den folgenden Bedingungen zu:
                                        </div>
                                        <div class="form-group ">
                                            <div class="form-check" style="padding-bottom: 5px;">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    id="dataprotection"
                                                    v-model="$v.form.step_5.urheberrecht.$model"
                                                    :class="{'is-invalid': $v.form.step_5.urheberrecht.$error}"
                                                >
                                                <label class="form-check-label" for="dataprotection">
                                                    Datenschutzerklärung
                                                    <router-link :to="{name: 'DataProtection'}" target="_blank" style="text-decoration: underline;">
                                                        (hier nachzulesen)
                                                    </router-link>
                                                </label>
                                            </div>
                                            <div class="form-check" style="padding-bottom: 5px;">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    id="anbs"
                                                    v-model="$v.form.step_5.allgemeine_nutzungsbedingungen.$model"
                                                    :class="{'is-invalid': $v.form.step_5.allgemeine_nutzungsbedingungen.$error}"
                                                >
                                                <label class="form-check-label" for="anbs">
                                                    Teilnahmebedingungen
                                                    <router-link :to="{name: 'ParticipationTerms'}" target="_blank" style="text-decoration: underline;">
                                                        (hier nachzulesen)
                                                    </router-link>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Step 5 -->
                            </transition>

                            <div class="form-block">
                                <div class="form-block__actions" :class="{'form-block__actions--start': formCurrentStep === 0}">
                                    <button
                                        v-if="formCurrentStep !== 0"
                                        class="btn btn--blue form-block__actions--back"
                                        @click="returnToPreviousStep"
                                    >
                                        Zurück
                                    </button>
                                    <button
                                        v-if="formCurrentStep !== (formSteps.length - 1)"
                                        class="btn btn--blue form-block__actions--next"
                                        @click="validateStep"
                                    >
                                        Weiter
                                    </button>
                                    <button
                                        v-if="formCurrentStep === (formSteps.length - 1)"
                                        class="btn btn--blue form-block__actions--submit"
                                        @click="submitProject"
                                    >
                                        Projekt einreichen
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div v-if="isProjectLoading || projectCreated" class="submit-project__created">
                    <div class="submit-project__created-inner">
                        <div class="submit-project__created-feedback">
                            <!-- loading -->
                            <div class="submit-project__created--text" >
                                <template v-if="isProjectLoading">
                                    <p>
                                        Ihre Daten werden an unser System übermittelt. Bitte warten Sie, bis die Übermittlung abgeschlossen ist. Abhängig von Dateigrößen und Internet-Geschwindigkeiten kann dies etwas länger dauern.
                                    </p>
                                </template>
                                <template v-if="projectCreated">
                                    <h2>
                                        Vielen Dank für Ihr Projekt!
                                    </h2>
                                    <p>
                                        Wir prüfen Ihre Daten und melden uns bei Ihnen, bevor wir Ihr Projekt veröffentlichen. Wir drücken jetzt schon die Daumen!
                                        Wenn Sie Fragen haben, melden Sie sich gerne unter <a href="mailto:redaktion@velbert-zeigt-herz.de">redaktion@velbert-zeigt-herz.de</a>
                                    </p>
                                </template>
                                <div class="submit-project__created--state">
                                    <transition name="project-loaded" mode="out-in">
                                        <div
                                            v-if="isProjectLoading"
                                            class="loader-spinner"
                                            key="loader"
                                        >
                                            <div></div><div></div><div></div><div></div>
                                        </div>
                                        <div v-if="projectCreated" class="submit-project__created-icon" key="checkmark">
                                            <span class="mdi mdi-check"></span>
                                        </div>
                                    </transition>
                                </div>
                            </div>
                            <!-- loading -->

                        </div>
                    </div>
                </div>
        </div>
    </div>
</template>

<script>
import VueScrollTo from 'vue-scrollto'

import { validationMixin } from 'vuelidate'
import { required, sameAs, minValue, minLength, maxLength, email, numeric } from 'vuelidate/lib/validators'

import InputCustom from '@/elements/InputCustom'
import TextareaCustom from '@/elements/TextareaCustom'
import InputFile from '@/elements/InputFile'
import RangeSlider from '@/elements/RangeSlider'

export default {
    name: 'SubmitProject',

    mixins: [
        validationMixin
    ],

    components: {
        InputCustom,
        TextareaCustom,
        InputFile,
        RangeSlider,
    },

    validations () {
        let validation = {
            form: {
                step_1: {
                    titel: {
                        required,
                        minLength: minLength(2),
                        maxLength: maxLength(255)
                    },
                    kurzbeschreibung: {
                        required,
                        maxLength: maxLength(160)
                    },
                    ausfuehrliche_beschreibung: {
                        required
                    },
                },
                step_2: {
                    bild: {
                        sizeValid: {
                            isValid: value => value === true
                        },
                        extValid: {
                            isValid: value => value === true
                        }
                    },
                    freistellungsbescheid: {
                        sizeValid: {
                            isValid: value => value === true
                        }
                    },
                    logo: {
                        sizeValid: {
                            isValid: value => value === true
                        },
                        extValid: {
                            isValid: value => value === true
                        }
                    }
                },
                step_3: {
                    projekttraeger_organisation: {
                        required,
                        minLength: minLength(2),
                        maxLength: maxLength(255)
                    },
                    projekttraeger_strasse: {
                        required,
                        minLength: minLength(2),
                        maxLength: maxLength(255)
                    },
                    projekttraeger_nummer: {
                        required
                    },
                    projekttraeger_plz: {
                        required,
                        numeric
                    },
                    projekttraeger_ort: {
                        required,
                        minLength: minLength(2),
                        maxLength: maxLength(255)
                    },
                    projekttraeger_vorname: {
                        required,
                        minLength: minLength(2),
                        maxLength: maxLength(255)
                    },
                    projekttraeger_nachname: {
                        required,
                        minLength: minLength(2),
                        maxLength: maxLength(255)
                    },
                    projekttraeger_telefonnummer: {
                        required,
                        minLength: minLength(2),
                        maxLength: maxLength(255)
                    },
                    projekttraeger_email: {
                        required,
                        email
                    },
                    projekttraeger_internetadresse: {
                        required,
                    },
                },
                step_4: {
                    spendenziel: {
                        minValue: minValue(10)
                    },
                    bankverbindung_iban: {
                        minLength: minLength(15),
                        maxLength: maxLength(34)
                    },
                    bankverbindung_bic: {
                        minLength: minLength(8),
                        maxLength: maxLength(11)
                    },
                },
                step_5: {
                    urheberrecht: {
                        sameAs: sameAs(() => true)
                    },
                    allgemeine_nutzungsbedingungen: {
                        sameAs: sameAs(() => true)
                    }
                }
            }
        }

        if (this.projectLocationVisible) {
            validation.form.step_3 = {
                ...validation.form.step_3,
                projektort_strasse: {
                    required,
                    minLength: minLength(2),
                    maxLength: maxLength(255)
                },
                projektort_nummer: {
                    required,
                },
                projektort_plz: {
                    required,
                    numeric
                },
                projektort_ort: {
                    required,
                    minLength: minLength(2),
                    maxLength: maxLength(255)
                }
            }
        }

        return validation
    },

    computed: {
        projectShowErrorDesc () {
            return this.$v.$error || this.projectNameAlreadyTaken || this.$v.form['step_' + (this.formCurrentStep + 1)].$error
        },
        progressBarWidthStep () {
            return 100 / this.formSteps.length * (this.formCurrentStep + 1)
        },
        isProjectLoading () {
            return this.$store.getters.getLoadingState('create')
        }
    },

    data () {
        return {
            projectLocationVisible: false,
            projectCreated: false,
            projectError: false,
            projectNameAlreadyTaken: false,
            urlInvalid: false,
            formSteps: [
                {
                    name: 'Allgemeine Informationen zu Ihrem Projekt'
                },
                {
                    name: 'Dateien'
                },
                {
                    name: 'Projektträger / Kontakt'
                },
                {
                    name: 'Spenden'
                },
                {
                    name: 'Datenschutz und Formularversand'
                }
            ],
            formCurrentStep: 0,
            form: {
                step_1: {
                    titel: '',
                    ausfuehrliche_beschreibung: '',
                    kurzbeschreibung: '',
                },
                step_2: {
                    bild: null,
                    bild_ki_generiert: false,
                    logo: null,
                    freistellungsbescheid: null,
                },
                step_3: {
                    projekttraeger_organisation: '',
                    projekttraeger_strasse: '',
                    projekttraeger_nummer: '',
                    projekttraeger_plz: '',
                    projekttraeger_ort: '',
                    projekttraeger_vorname: '',
                    projekttraeger_nachname: '',
                    projekttraeger_telefonnummer: '',
                    projekttraeger_email: '',
                    projekttraeger_internetadresse: '',
                },
                step_4: {
                    spendenziel: 0,
                    bankverbindung_iban: '',
                    bankverbindung_bic: '',
                },
                step_5: {
                    urheberrecht: false,
                    allgemeine_nutzungsbedingungen: false
                },
                projektort_strasse: '',
                projektort_nummer: '',
                projektort_plz: '',
                projektort_ort: '',
            },
            swipeDirection: ''
        }
    },

    methods: {

        setFile (key, files) {
            this.form['step_' + (this.formCurrentStep + 1)][key] = files[0]
            if (!this.$v.form['step_' + (this.formCurrentStep + 1)][key]) {
                return
            }

            this.$v.form['step_' + (this.formCurrentStep + 1)][key].$touch()
        },

        convertFormData (data) {
            const convertedObject = this.convertFormStepsInOneObject(data)
            const formData = new FormData()

            // Always send 0 or 1 so an unchecked box has an unambiguous value.
            formData.set('bild_ki_generiert', convertedObject.bild_ki_generiert ? '1' : '0')

            Object.entries(convertedObject).map(entry => {
                const key = entry[0]
                const value = typeof entry[1] === 'boolean' ? ~~entry[1] : entry[1]
                // Check if value is null then return
                if (!value || key === 'bild_ki_generiert') {
                    return
                }
                // Check if we have an file
                if (value.file && value.file instanceof File) {
                    return formData.append(key, value.file)
                }

                // Check if we have spendenziel (needs to be transformed as backend thinks spendenziel value is in hearts)
                if (key === 'spendenziel') {
                    return formData.set(key, this.convertEuroToHearts(value))
                }

                return formData.set(key, value)
            })

            return formData
        },

        convertEuroToHearts (value) {
            // 1 Heart === 0.5 €
            return value * 2
        },

        convertFormStepsInOneObject (form) {
            let convertedObject = {}
            Object.entries(form)
                .map(entry => {
                    for (let key in entry[1]) {
                        convertedObject = {
                            ...convertedObject,
                            [key]: entry[1][key]
                        }
                    }
                })
            return convertedObject
        },

        submitProject () {
            // Make sure validation did trigger
            this.$v.$touch()
            // Check validation stats
            if (this.$v.$invalid) {
                this.scrollTo('#error-desc')
                return
            }

            const data = this.convertFormData(this.form)

            console.log('valid', data)

            this.$store.dispatch('createProject', data).then(response => {
                if (response.status && response.status === 201) {
                    this.projectCreated = true
                    this.projectNameAlreadyTaken = false
                    this.urlInvalid = false
                    this.projectError = false
                } else if (response.status && response.status === 422) {
                    this.projectNameAlreadyTaken = response.data.errors.titel != null && response.data.errors.titel[0] === 'The titel has already been taken.' ? this.form.step_1.titel : false
                    this.urlInvalid = response.data.errors.projekttraeger_internetadresse != null
                    if (this.projectNameAlreadyTaken) {
                        this.formCurrentStep = 0
                    }

                    if (!this.projectNameAlreadyTaken && this.urlInvalid) {
                        this.formCurrentStep = 2
                    }

                    this.scrollTo('#submit-form')
                } else {
                    // TODO: Add proper reaction title already exists error
                    console.log(response)
                    this.projectError = true
                    this.scrollTo('#error-desc-unknown')
                }
            })
        },

        scrollTo (selector) {
            const options = {
                container: 'body',
                easing: 'ease-out',
                offset: -100,
                force: true
            }

            this.$nextTick().then(() => {
                VueScrollTo.scrollTo(selector, 500, options)
            })
        },

        validateStep () {
            this.$v.form['step_' + (this.formCurrentStep + 1)].$touch()

            if (this.$v.form['step_' + (this.formCurrentStep + 1)].$invalid) {
                this.scrollTo('#error-desc')
                return
            }

            if (this.$v.form['step_' + (this.formCurrentStep + 1)].$invalid) {
                this.scrollTo('#error-desc')
                return
            }
            this.animateFadeOutLeft()
            this.formCurrentStep += 1
            this.scrollTo('#submit-form')
        },

        returnToPreviousStep () {
            this.animateFadeOutRight()
            this.formCurrentStep -= 1
            this.scrollTo('#submit-form')
        },

        animateFadeOutLeft () {
            this.swipeDirection = 'fade-out-left'
        },

        animateFadeOutRight () {
            this.swipeDirection = 'fade-out-right'
        }
    }
}
</script>

<style lang="scss">

.submit-project__created-feedback h2 {
    font-size: 30px;
    font-weight: bold;
    text-align: center;
    color: #004494;
}
.submit-project__created-icon {
    display: block;
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto;
    background-color: #004494;
    border-radius: 50%;
    text-align: center;
    padding-top: 3px;

    span {
        color: #fff;
    }

}

#donation-target__wrapper {

    border: 2px solid transparent;

    &.is-invalid {
        border: 2px solid #d11212;
        color: #d11212;
    }
}
.fade-out-left-enter-active,
.fade-out-left-leave-active,
.fade-out-right-enter-active,
.fade-out-right-leave-active {
    animation-duration: 0.5s;
    transition: opacity 0.5s ease-in-out;
}

.fade-out-left-enter, .fade-out-left-leave-to {
    animation-name: fadeOutLeft;
    opacity: 0;
}

.fade-out-left-enter-to {
    animation-name: fadeOutRight;
    animation-direction: reverse;
    opacity: 1;
}

.fade-out-right-enter, .fade-out-right-leave-to {
    animation-name: fadeOutRight;
    opacity: 0;
}

.fade-out-right-enter-to {
    animation-name: fadeOutLeft;
    animation-direction: reverse;
    opacity: 1;
}

@keyframes fadeOutLeft {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(-35%);
    }
}

@keyframes fadeOutRight {
    0% {
        transform: translateX(0);
    }

    100% {
        transform: translateX(35%);
    }
}

.loader-spinner {
    display: block;
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto;
}

.loader-spinner div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 60px;
    height: 60px;
    margin: 8px;
    border: 4px solid #004494;
    border-radius: 50%;
    animation: loader-spinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #004494 transparent transparent transparent;
}

.loader-spinner div:nth-child(1) {
    animation-delay: -0.45s;
}

.loader-spinner div:nth-child(2) {
    animation-delay: -0.3s;
}

.loader-spinner div:nth-child(3) {
    animation-delay: -0.15s;
}

@keyframes loader-spinner {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>



// WEBPACK FOOTER //
// src/components/SubmitProject.vue